import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock,
  CheckCircle,
  XCircle,
  BedDouble,
  CreditCard,
  MapPin,
  IndianRupee,
  Calendar,
  LogIn,
  ArrowRight,
  RefreshCw,
  Inbox,
  ChevronDown,
  ChevronUp,
  Home,
  Tag,
} from 'lucide-react';
import PublicLayout from '../../components/layout/PublicLayout';
import SEO from '../../components/SEO';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { userAPI } from '../../services/api';
import { useUser } from '../../context/UserContext';

// ─── helpers ────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  payment_pending: {
    label: 'Payment Pending',
    icon: CreditCard,
    bg: 'bg-orange-100',
    text: 'text-orange-700',
    border: 'border-orange-200',
    dot: 'bg-orange-400',
  },
  pending: {
    label: 'Awaiting Approval',
    icon: Clock,
    bg: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    dot: 'bg-yellow-400',
  },
  approved: {
    label: 'Approved',
    icon: CheckCircle,
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-200',
    dot: 'bg-green-400',
  },
  checked_in: {
    label: 'Checked In',
    icon: BedDouble,
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    border: 'border-blue-200',
    dot: 'bg-blue-400',
  },
  checked_out: {
    label: 'Checked Out',
    icon: CheckCircle,
    bg: 'bg-indigo-100',
    text: 'text-indigo-700',
    border: 'border-indigo-200',
    dot: 'bg-indigo-400',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-200',
    dot: 'bg-red-400',
  },
  cancelled: {
    label: 'Cancelled',
    icon: XCircle,
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    border: 'border-gray-200',
    dot: 'bg-gray-400',
  },
};

const fmt = (iso) =>
  new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

// ─── StatusBadge ────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

// ─── BookingCard ─────────────────────────────────────────────────────────────

const BookingCard = ({ booking }) => {
  const [expanded, setExpanded] = useState(false);

  const isActive = ['payment_pending', 'pending', 'approved', 'checked_in'].includes(
    booking.status
  );

  return (
    <div
      className={`rounded-2xl border bg-white shadow-sm transition-all duration-300 overflow-hidden ${
        isActive ? 'border-railway-200 ring-1 ring-railway-100' : 'border-midnight-100'
      }`}
    >
      {/* ── Card Header ─────────────────────────────── */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          {/* Station + category */}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-railway-50 flex items-center justify-center shrink-0">
              <MapPin className="w-5 h-5 text-railway-500" />
            </div>
            <div>
              <p className="font-display font-bold text-midnight-900 leading-tight">
                {booking.station?.name || 'Station'}
              </p>
              <p className="text-xs text-midnight-500 mt-0.5">
                {booking.station?.code} &middot;{' '}
                {booking.requestedCategory?.name ||
                  booking.allocatedCategory?.name ||
                  'Room'}
              </p>
            </div>
          </div>

          <StatusBadge status={booking.status} />
        </div>

        {/* Key info row */}
        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
          <div className="rounded-xl bg-midnight-50 px-3 py-2">
            <p className="text-xs text-midnight-400 mb-0.5">Duration</p>
            <p className="font-bold text-midnight-800 text-sm">{booking.durationSlot}</p>
          </div>

          <div className="rounded-xl bg-midnight-50 px-3 py-2">
            <p className="text-xs text-midnight-400 mb-0.5">Amount</p>
            <p className="font-bold text-midnight-800 text-sm flex items-center justify-center gap-0.5">
              <IndianRupee className="w-3 h-3" />
              {booking.totalAmount > 0 ? booking.totalAmount : '—'}
            </p>
          </div>

          <div className="rounded-xl bg-midnight-50 px-3 py-2">
            <p className="text-xs text-midnight-400 mb-0.5">Booked</p>
            <p className="font-bold text-midnight-800 text-sm">
              {fmtDate(booking.requestedAt || booking.createdAt)}
            </p>
          </div>
        </div>

        {/* Action row */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <Link
            to={`/booking/status/${booking._id}`}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-railway-600 hover:text-railway-700 transition-colors"
          >
            View Details
            <ArrowRight className="w-4 h-4" />
          </Link>

          <button
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-midnight-400 hover:text-midnight-600 transition-colors"
          >
            {expanded ? (
              <>
                Less <ChevronUp className="w-3.5 h-3.5" />
              </>
            ) : (
              <>
                More <ChevronDown className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Expanded Details ─────────────────────────── */}
      {expanded && (
        <div className="border-t border-midnight-100 px-5 py-4 bg-midnight-50 space-y-3 text-sm animate-slide-up">
          {/* Booking ref */}
          <div className="flex justify-between">
            <span className="text-midnight-500">Booking Ref</span>
            <span className="font-mono font-semibold text-midnight-800 text-xs">{booking._id}</span>
          </div>

          {/* PNR */}
          <div className="flex justify-between">
            <span className="text-midnight-500">PNR</span>
            <span className="font-medium text-midnight-800">{booking.prn || '—'}</span>
          </div>

          {/* Scheduled check-in */}
          {booking.scheduledDate && (
            <div className="flex justify-between">
              <span className="text-midnight-500">Scheduled Date</span>
              <span className="font-medium text-midnight-800">
                {fmtDate(booking.scheduledDate)}
                {booking.scheduledTimeSlot && ` at ${booking.scheduledTimeSlot}`}
              </span>
            </div>
          )}

          {/* Allocated room */}
          {booking.allocatedRoom && (
            <div className="flex justify-between">
              <span className="text-midnight-500">Room</span>
              <span className="font-medium text-midnight-800">
                #{booking.allocatedRoom.roomNumber}
                {booking.allocatedRoom.floor ? ` · Floor ${booking.allocatedRoom.floor}` : ''}
              </span>
            </div>
          )}

          {/* Payment status */}
          <div className="flex justify-between">
            <span className="text-midnight-500">Payment</span>
            <span
              className={`font-semibold ${
                booking.paymentStatus === 'COMPLETED'
                  ? 'text-green-600'
                  : booking.paymentStatus === 'FAILED'
                  ? 'text-red-600'
                  : 'text-orange-600'
              }`}
            >
              {booking.paymentStatus || 'PENDING'}
            </span>
          </div>

          {/* Check-in / check-out timestamps */}
          {booking.checkInAt && (
            <div className="flex justify-between">
              <span className="text-midnight-500">Check-in</span>
              <span className="font-medium text-midnight-800">{fmt(booking.checkInAt)}</span>
            </div>
          )}
          {booking.checkOutAt && (
            <div className="flex justify-between">
              <span className="text-midnight-500">Check-out</span>
              <span className="font-medium text-midnight-800">{fmt(booking.checkOutAt)}</span>
            </div>
          )}

          {/* Bill breakdown */}
          {booking.totalAmount > 0 && (
            <div className="pt-3 border-t border-midnight-200 space-y-1.5">
              {booking.price > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-midnight-500">Room Charges</span>
                  <span className="font-medium text-midnight-800 flex items-center gap-0.5">
                    <IndianRupee className="w-3 h-3" />{booking.price}
                  </span>
                </div>
              )}
              {booking.gstPercentage > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-midnight-500">GST ({booking.gstPercentage}%)</span>
                  <span className="font-medium text-midnight-800 flex items-center gap-0.5">
                    <IndianRupee className="w-3 h-3" />{booking.gstAmount}
                  </span>
                </div>
              )}
              {booking.extraCharges > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-midnight-500">Extra Charges</span>
                  <span className="font-medium text-midnight-800 flex items-center gap-0.5">
                    <IndianRupee className="w-3 h-3" />{booking.extraCharges}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm font-bold border-t border-midnight-200 pt-1.5">
                <span className="text-midnight-700">Total Paid</span>
                <span className="text-midnight-900 flex items-center gap-0.5">
                  <IndianRupee className="w-3.5 h-3.5" />{booking.totalAmount}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── FILTER TABS ─────────────────────────────────────────────────────────────

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active', statuses: ['payment_pending', 'pending', 'approved', 'checked_in'] },
  { key: 'completed', label: 'Completed', statuses: ['checked_out'] },
  { key: 'cancelled', label: 'Cancelled/Rejected', statuses: ['cancelled', 'rejected'] },
];

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

const MyBookings = () => {
  const { isAuthenticated, user, loading: authLoading } = useUser();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const fetchBookings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await userAPI.getMyBookings();
      setBookings(res.data.bookings || []);
    } catch (err) {
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) fetchBookings();
    else if (!authLoading) setLoading(false);
  }, [isAuthenticated, authLoading]);

  const filtered = bookings.filter((b) => {
    const f = FILTERS.find((f) => f.key === activeFilter);
    if (!f || !f.statuses) return true;
    return f.statuses.includes(b.status);
  });

  // counts for tabs
  const counts = FILTERS.reduce((acc, f) => {
    acc[f.key] = f.statuses
      ? bookings.filter((b) => f.statuses.includes(b.status)).length
      : bookings.length;
    return acc;
  }, {});

  return (
    <PublicLayout>
      <SEO
        title="My Bookings"
        description="View all your retiring room booking history, status, and payment details."
        noIndex
      />

      <div className="min-h-[70vh] bg-gradient-to-b from-midnight-50 to-white py-10">
        <div className="max-w-2xl mx-auto px-4">

          {/* ── Page header ─────────────────────────── */}
          <div className="mb-8 animate-slide-up">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-railway-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-railway-600" />
              </div>
              <h1 className="text-2xl font-display font-bold text-midnight-900">
                My Bookings
              </h1>
            </div>
            {user && (
              <p className="text-midnight-500 ml-13 pl-0.5 text-sm">
                Welcome back, <span className="font-semibold text-midnight-700">{user.name}</span>
              </p>
            )}
          </div>

          {/* ── Not logged in ───────────────────────── */}
          {!authLoading && !isAuthenticated && (
            <div className="card py-16 text-center animate-slide-up">
              <LogIn className="w-14 h-14 text-midnight-300 mx-auto mb-4" />
              <h2 className="text-xl font-display font-bold text-midnight-800 mb-2">
                Login to view your bookings
              </h2>
              <p className="text-midnight-500 mb-6 text-sm max-w-xs mx-auto">
                Sign in to see all your past and current room reservations.
              </p>
              <Link to="/user/login" className="btn-primary inline-flex items-center gap-2">
                <LogIn className="w-4 h-4" />
                Login / Sign Up
              </Link>
            </div>
          )}

          {/* ── Loading ─────────────────────────────── */}
          {isAuthenticated && loading && (
            <div className="flex justify-center py-20">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {/* ── Error ───────────────────────────────── */}
          {isAuthenticated && !loading && error && (
            <div className="card py-12 text-center">
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <p className="text-midnight-600 mb-4">{error}</p>
              <button onClick={fetchBookings} className="btn-secondary inline-flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
            </div>
          )}

          {/* ── Bookings list ────────────────────────── */}
          {isAuthenticated && !loading && !error && (
            <>
              {/* Filter tabs + refresh */}
              <div className="flex items-center justify-between gap-3 mb-5">
                <div className="flex gap-2 flex-wrap">
                  {FILTERS.map((f) => (
                    <button
                      key={f.key}
                      onClick={() => setActiveFilter(f.key)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                        activeFilter === f.key
                          ? 'bg-railway-600 text-white shadow-sm'
                          : 'bg-midnight-100 text-midnight-600 hover:bg-midnight-200'
                      }`}
                    >
                      {f.label}
                      {counts[f.key] > 0 && (
                        <span
                          className={`ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] ${
                            activeFilter === f.key
                              ? 'bg-white text-railway-600'
                              : 'bg-midnight-200 text-midnight-600'
                          }`}
                        >
                          {counts[f.key]}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                <button
                  onClick={fetchBookings}
                  className="text-midnight-400 hover:text-railway-600 transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>

              {/* Empty state */}
              {filtered.length === 0 && (
                <div className="card py-16 text-center">
                  <Inbox className="w-14 h-14 text-midnight-200 mx-auto mb-4" />
                  <h3 className="text-lg font-display font-semibold text-midnight-700 mb-2">
                    No bookings here
                  </h3>
                  <p className="text-midnight-400 text-sm mb-6">
                    {activeFilter === 'all'
                      ? "You haven't made any bookings yet."
                      : 'No bookings match this filter.'}
                  </p>
                  <Link to="/" className="btn-primary inline-flex items-center gap-2">
                    <Home className="w-4 h-4" />
                    Book a Room
                  </Link>
                </div>
              )}

              {/* Cards */}
              <div className="space-y-4">
                {filtered.map((b) => (
                  <BookingCard key={b._id} booking={b} />
                ))}
              </div>

              {/* Footer link */}
              {filtered.length > 0 && (
                <div className="mt-8 text-center">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sm text-railway-600 hover:text-railway-700 font-medium"
                  >
                    <Home className="w-4 h-4" />
                    Book another room
                  </Link>
                </div>
              )}
            </>
          )}

        </div>
      </div>
    </PublicLayout>
  );
};

export default MyBookings;
